import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { TrafficTicket, TicketStatus } from '../types';
import { ArrowLeft, CreditCard, Lock, AlertCircle, CheckCircle, Download } from 'lucide-react';
import { format } from 'date-fns';
import { ReceiptService } from '../services/ReceiptService';

// Initialize Stripe with publishable key
const stripePromise = loadStripe('pk_test_51TAA1mRodny6iW8vCJf20W2gdSIVYZPtQNNyFp8fEpEVyCU4QE32Zr1lfIXBCe1jpuvCrfD88TJhzuh9GQFHFTsf00T9QCmRPd');

const PaymentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TrafficTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchTicketDetails();
  }, [id]);

  const fetchTicketDetails = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const ticketDoc = await getDoc(doc(db, 'trafficTickets', id));
      
      if (ticketDoc.exists()) {
        const data = ticketDoc.data();
        const ticketData: TrafficTicket = {
          id: ticketDoc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          paymentDate: data.paymentDate?.toDate(),
          violatorDOB: data.violatorDOB?.toDate(),
          offenceTime: data.offenceTime?.toDate(),
        } as TrafficTicket;
        
        setTicket(ticketData);
        
        // Check if already paid
        if (ticketData.status === TicketStatus.PAID) {
          setSuccess(true);
        }
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
      setError('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Ticket not found</h3>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          Go back
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-green-600 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
          <p className="text-gray-600 mb-6">
            Your payment for ticket {ticket.ticketCode || ticket.id.slice(0, 8)} has been processed successfully.
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => ReceiptService.downloadReceipt(ticket)}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Download size={20} />
              Download Receipt
            </button>
            <button
              onClick={() => navigate(`/tickets/${ticket.id}`)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              View Ticket Details
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pay Ticket</h1>
          <p className="text-gray-600 mt-1">
            Ticket {ticket.ticketCode || ticket.id.slice(0, 8)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Elements stripe={stripePromise}>
            <PaymentForm
              ticket={ticket}
              onPaymentSuccess={() => setSuccess(true)}
              onProcessingChange={setProcessing}
              onError={setError}
            />
          </Elements>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Ticket Code</span>
                <span className="font-medium">{ticket.ticketCode || ticket.id.slice(0, 8)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Offence</span>
                <span className="font-medium">{ticket.offenceType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Violator</span>
                <span className="font-medium">{ticket.violatorName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date Issued</span>
                <span className="font-medium">{format(ticket.createdAt, 'MMM dd, yyyy')}</span>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                  <span className="text-2xl font-bold text-blue-600">
                    EC${ticket.fineAmount?.toLocaleString() || '0'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-start gap-3">
              <Lock className="text-blue-600 mt-1" size={20} />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Secure Payment</h3>
                <p className="text-sm text-blue-700">
                  Your payment information is encrypted and secure. We use Stripe's 
                  industry-leading security to protect your data.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentForm: React.FC<{
  ticket: TrafficTicket;
  onPaymentSuccess: () => void;
  onProcessingChange: (processing: boolean) => void;
  onError: (error: string | null) => void;
}> = ({ ticket, onPaymentSuccess, onProcessingChange, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      setCardError('Payment system is not ready. Please refresh the page and try again.');
      return;
    }

    // Get the card element
    const cardElement = elements.getElement(CardElement);
    
    if (!cardElement) {
      setCardError('Card information is not complete. Please check your card details.');
      return;
    }

    setProcessing(true);
    onProcessingChange(true);
    onError(null);
    setCardError(null);

    try {
      // Create payment intent on backend
      const response = await fetch('https://us-central1-caribbean-traffic-tickets.cloudfunctions.net/stripePayments/create-payment-sheet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round((ticket.fineAmount || 0) * 100), // Convert to cents
          currency: 'XCD', // Eastern Caribbean Dollar
          ticketId: ticket.id,
          violatorName: ticket.violatorName,
          offenceType: ticket.offenceType,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend error:', errorText);
        throw new Error('Failed to create payment intent. Please try again.');
      }

      const { paymentIntentClientSecret } = await response.json();

      if (!paymentIntentClientSecret) {
        throw new Error('Invalid response from payment server');
      }

      // Confirm payment with Stripe
      const { error: confirmError } = await stripe.confirmCardPayment(paymentIntentClientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: ticket.violatorName || 'Cardholder',
          },
        },
      });

      if (confirmError) {
        console.error('Stripe confirm error:', confirmError);
        throw new Error(confirmError.message || 'Payment failed');
      }

      // Payment successful - update ticket status directly as backup
      await updateTicketStatusDirectly(ticket.id);
      
      onPaymentSuccess();
    } catch (error) {
      console.error('Payment error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Payment failed. Please try again.';
      setCardError(errorMessage);
      onError(errorMessage);
    } finally {
      setProcessing(false);
      onProcessingChange(false);
    }
  };

  const updateTicketStatusDirectly = async (ticketId: string) => {
    try {
      const ticketRef = doc(db, 'trafficTickets', ticketId);
      await updateDoc(ticketRef, {
        status: TicketStatus.PAID,
        paymentDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log(`Updated ticket ${ticketId} status to paid directly`);
    } catch (error) {
      console.error('Error updating ticket status directly:', error);
      // Don't throw error - payment was successful, just log the issue
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
      <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
        <CreditCard size={20} />
        Payment Information
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Card Details
          </label>
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-colors">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                    iconColor: '#666EE8',
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                  complete: {
                    color: '#424770',
                  },
                },
                hidePostalCode: true,
              }}
            />
          </div>
        </div>

        {cardError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-red-600 mt-1" size={20} />
              <div>
                <h3 className="font-semibold text-red-900 mb-1">Payment Error</h3>
                <p className="text-sm text-red-700">{cardError}</p>
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={!stripe || !elements || processing}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Processing...
            </>
          ) : (
            'Pay EC' + (ticket.fineAmount?.toLocaleString() || '0')
          )}
        </button>

        <div className="text-center text-sm text-gray-500">
          <p>By completing this payment, you agree to the terms of service.</p>
          <p className="mt-1">Powered by Stripe</p>
        </div>
      </form>
    </div>
  );
};

export default PaymentPage;
